// ATS — Anexo Transaccional Simplificado (SRI Ecuador). Previsualización de
// cabecera/compras/ventas/establecimientos/anulados + validador + descarga del
// XML mensual. Contabilidad → Reportes → ATS. Insumo referencial: validar contra
// el esquema oficial del SRI antes de cargar en DIMM/línea.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, FileCode, Loader2, AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { exportarCSV, field, usd, rpc } from './shared';
import { LOCALE } from '@/lib/utils/regional';

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const HOY = new Date();
const num = (v: unknown) => Number(v ?? 0).toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface AtsCompra { cod_sustento: string; tp_id: string; id_prov: string; proveedor: string; tipo_comprobante: string; establecimiento: string; punto_emision: string; secuencial: string; fecha_emision: string; autorizacion: string; base_imponible: number; monto_iva: number; ret_iva: number; air: { cod: string; base: number; pct: number; valor: number }[] }
interface AtsVenta { tp_id: string; id_cliente: string; tipo_comprobante: string; n_comprobantes: number; base_grav: number; base_iva0: number; monto_iva: number }
interface AtsEstab { estab: string; ventas: number }
interface AtsAnulado { tipo_comprobante: string; establecimiento: string; punto_emision: string; secuencial: string; autorizacion: string }
interface AtsData {
  cabecera: { ruc: string; razon_social: string; anio: number; mes: string; num_estab: string; total_ventas: number };
  compras: AtsCompra[]; ventas: AtsVenta[]; ventas_estab: AtsEstab[]; anulados: AtsAnulado[]; validacion: string[];
}
interface Cuadre { ats: number; form_104: number; diferencia: number; excede: boolean }
interface Preflight {
  tolerancia: number; bloquea: boolean; motivos: string[];
  cuadre: { ventas: Cuadre; compras: Cuadre };
  sin_clasificar: { recibidos: number; recibidos_monto: number; emitidos: number; emitidos_monto: number; explica_compras: boolean };
  datos: { sin_sustento: number; sin_autorizacion: number; id_invalido: number };
}

const TCOMP: Record<string, string> = { '01': 'Factura', '04': 'Nota de crédito', '05': 'Nota de débito', '07': 'Comp. retención', '18': 'Factura' };
const TID: Record<string, string> = { '01': 'RUC', '02': 'Cédula', '03': 'Pasaporte', '07': 'Consumidor final' };

const Card2 = ({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) => (
  <Card><CardHeader className="pb-2 flex-row items-center justify-between"><CardTitle className="text-base">{title}</CardTitle>{right}</CardHeader><CardContent className="overflow-x-auto">{children}</CardContent></Card>
);
const CsvBtn = ({ onClick }: { onClick: () => void }) => (
  <button onClick={onClick} className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-sm hover:bg-muted"><Download className="w-4 h-4" /> CSV</button>
);

export function ATS() {
  const [anio, setAnio] = useState(HOY.getFullYear());
  const [mes, setMes] = useState(HOY.getMonth() + 1);
  const [bajando, setBajando] = useState(false);
  const [omitir, setOmitir] = useState(false);

  const q = useQuery<AtsData>({ queryKey: ['ats', anio, mes], queryFn: () => rpc<AtsData>('trib_ats_datos', { _anio: anio, _mes: mes }) });
  const qp = useQuery<Preflight>({ queryKey: ['ats-preflight', anio, mes], queryFn: () => rpc<Preflight>('trib_ats_preflight', { _anio: anio, _mes: mes }) });
  const d = q.data;
  const p = qp.data;
  const bloqueado = !!p?.bloquea && !omitir;

  const descargarXml = async () => {
    setBajando(true);
    try {
      const xml = await rpc<string>('trib_ats_xml', { _anio: anio, _mes: mes });
      const url = URL.createObjectURL(new Blob([xml], { type: 'application/xml;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url; a.download = `ATS_${d?.cabecera.ruc || ''}_${anio}${String(mes).padStart(2, '0')}.xml`; a.click();
      URL.revokeObjectURL(url);
    } finally { setBajando(false); }
  };

  const nComp = d?.compras.length ?? 0;
  const nVent = d?.ventas.reduce((s, v) => s + Number(v.n_comprobantes), 0) ?? 0;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-semibold flex items-center gap-2"><FileCode className="w-5 h-5 text-primary" /> ATS — Anexo Transaccional Simplificado</h1>
        <button onClick={descargarXml} disabled={bajando || q.isFetching || bloqueado} title={bloqueado ? 'Hay observaciones que impiden generar el anexo — revísalas abajo' : undefined} className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground hover:opacity-90 disabled:opacity-50">
          {bajando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Descargar XML
        </button>
      </div>
      <p className="text-sm text-muted-foreground">Insumo mensual referencial generado desde Financiero (solo comprobantes autorizados). Verifica los valores y el esquema contra el SRI antes de cargarlo en el DIMM / línea.</p>

      <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4">
        <label className="flex flex-col text-xs text-muted-foreground">Año<input type="number" className={`${field} w-28 mt-1`} value={anio} onChange={(e) => { setAnio(Number(e.target.value)); setOmitir(false); }} /></label>
        <label className="flex flex-col text-xs text-muted-foreground">Mes<select className={`${field} w-40 mt-1`} value={mes} onChange={(e) => { setMes(Number(e.target.value)); setOmitir(false); }}>{MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}</select></label>
      </CardContent></Card>

      {/* Revisión previa — lo que el SRI va a cruzar y lo que aún no se declara */}
      {p && <Card className={p.bloquea ? 'border-red-500/60' : ''}>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
          {p.bloquea ? <><ShieldAlert className="w-4 h-4 text-red-600" /> Revisión previa — hay que corregir antes de generar</>
                     : <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Revisión previa</>}
        </CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {/* El cruce que hace el SRI por su cuenta */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Cruce contra el Formulario 104</th><th className="py-2 pr-3 text-right">ATS</th><th className="py-2 pr-3 text-right">Form. 104</th><th className="py-2 pr-3 text-right">Diferencia</th></tr></thead>
              <tbody>
                {([['Ventas', p.cuadre.ventas], ['Compras', p.cuadre.compras]] as [string, Cuadre][]).map(([t, c]) => (
                  <tr key={t} className="border-b last:border-0">
                    <td className="py-1.5 pr-3">{t}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(c.ats)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{num(c.form_104)}</td>
                    <td className={`py-1.5 pr-3 text-right font-mono tabular-nums ${c.excede ? 'text-red-600 font-semibold' : Number(c.diferencia) !== 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{num(c.diferencia)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-muted-foreground mt-1">El SRI cruza estas cifras por su cuenta. Se bloquea la descarga si la diferencia supera {usd(p.tolerancia)} (configurable por empresa en Administración → Empresas).</p>
          </div>

          {/* Lo que existe pero aún no entra al anexo */}
          {(p.sin_clasificar.recibidos > 0 || p.sin_clasificar.emitidos > 0) && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
              <div className="flex items-start gap-2 text-amber-700 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <div className="font-medium">Comprobantes sin clasificar — no se declararán</div>
                  {p.sin_clasificar.recibidos > 0 && <div>{p.sin_clasificar.recibidos} recibido(s) del SRI por {usd(p.sin_clasificar.recibidos_monto)} que aún no son una compra registrada.</div>}
                  {p.sin_clasificar.emitidos > 0 && <div>{p.sin_clasificar.emitidos} emitido(s) por {usd(p.sin_clasificar.emitidos_monto)} sin venta correspondiente en el sistema.</div>}
                  {p.sin_clasificar.explica_compras && <div className="text-xs">Suele ser la causa de que las compras no cuadren con el 104.</div>}
                </div>
              </div>
            </div>
          )}

          {/* Lo que el DIMM rechaza al cargar el archivo */}
          {(p.datos.sin_sustento > 0 || p.datos.sin_autorizacion > 0 || p.datos.id_invalido > 0) && (
            <ul className="space-y-1 text-sm text-red-600">
              {p.datos.sin_sustento > 0 && <li className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 shrink-0" /> {p.datos.sin_sustento} compra(s) sin código de sustento — el DIMM rechaza el archivo.</li>}
              {p.datos.sin_autorizacion > 0 && <li className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 shrink-0" /> {p.datos.sin_autorizacion} compra(s) sin número de autorización.</li>}
              {p.datos.id_invalido > 0 && <li className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 shrink-0" /> {p.datos.id_invalido} compra(s) con identificación inválida para su tipo (RUC 13, cédula 10 dígitos).</li>}
            </ul>
          )}

          {p.bloquea
            ? <label className="flex items-start gap-2 text-sm border-t pt-3">
                <input type="checkbox" className="mt-0.5" checked={omitir} onChange={(e) => setOmitir(e.target.checked)} />
                <span>Generar el XML de todos modos, bajo mi responsabilidad — {p.motivos.join('; ')}.</span>
              </label>
            : <div className="flex items-center gap-2 text-sm text-emerald-600 border-t pt-3"><CheckCircle2 className="w-4 h-4" /> Nada impide generar el anexo de este período.</div>}
        </CardContent>
      </Card>}

      {q.isFetching ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary my-10" /> : d && <>
        {/* Observaciones del contenido — las que ya no dice la revisión previa */}
        {(() => {
          // Lo que el panel de arriba ya muestra no se repite aquí.
          const otras = d.validacion.filter((v) => !/^EL ATS NO CUADRA|^RUC del informante|^Compras sin número de autorización/.test(v));
          return otras.length === 0 ? null : (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Otras observaciones</CardTitle></CardHeader>
              <CardContent>
                <ul className="space-y-1">{otras.map((v, i) => <li key={i} className="flex items-center gap-2 text-sm text-amber-600"><AlertTriangle className="w-4 h-4 shrink-0" /> {v}</li>)}</ul>
              </CardContent>
            </Card>
          );
        })()}

        {/* Cabecera */}
        <Card2 title="Cabecera del informante">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
            <div><span className="text-muted-foreground">RUC:</span> <span className="font-mono">{d.cabecera.ruc}</span></div>
            <div className="md:col-span-2"><span className="text-muted-foreground">Razón social:</span> {d.cabecera.razon_social}</div>
            <div><span className="text-muted-foreground">Período:</span> {MESES[mes - 1]} {d.cabecera.anio}</div>
            <div><span className="text-muted-foreground"># Establecimientos:</span> {d.cabecera.num_estab}</div>
            <div><span className="text-muted-foreground">Total ventas:</span> {usd(d.cabecera.total_ventas)}</div>
            <div><span className="text-muted-foreground">Comprobantes de venta:</span> {nVent}</div>
            <div><span className="text-muted-foreground">Comprobantes de compra:</span> {nComp}</div>
          </div>
        </Card2>

        {/* Compras */}
        <Card2 title={`Compras (${nComp})`} right={<CsvBtn onClick={() => exportarCSV(`ats-compras-${anio}-${mes}`, ['Sustento', 'TipoID', 'Proveedor', 'RUC', 'Comprobante', 'Estab-PtoEmi-Sec', 'Fecha', 'Autorización', 'Base', 'IVA', 'Ret. IVA'], d.compras.map((c) => [c.cod_sustento, TID[c.tp_id] || c.tp_id, c.proveedor, c.id_prov, TCOMP[c.tipo_comprobante] || c.tipo_comprobante, `${c.establecimiento}-${c.punto_emision}-${c.secuencial}`, c.fecha_emision, c.autorizacion, c.base_imponible, c.monto_iva, c.ret_iva]))} />}>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Sust.</th><th className="py-2 pr-3">Proveedor</th><th className="py-2 pr-3">Comprobante</th><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3 text-right">Base</th><th className="py-2 pr-3 text-right">IVA</th><th className="py-2 pr-3 text-right">Ret. IVA</th><th className="py-2 pr-3 text-right">Ret. Renta</th></tr></thead>
            <tbody>
              {d.compras.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin compras con retención en el período</td></tr>}
              {d.compras.map((c, i) => (
                <tr key={i} className="border-b last:border-0 align-top">
                  <td className="py-1.5 pr-3 font-mono">{c.cod_sustento}</td>
                  <td className="py-1.5 pr-3">{c.proveedor}<div className="text-xs text-muted-foreground font-mono">{c.id_prov}</div></td>
                  <td className="py-1.5 pr-3">{TCOMP[c.tipo_comprobante] || c.tipo_comprobante}<div className="text-xs text-muted-foreground font-mono">{c.establecimiento}-{c.punto_emision}-{c.secuencial}</div></td>
                  <td className="py-1.5 pr-3 font-mono">{c.fecha_emision}</td>
                  <td className="py-1.5 pr-3 text-right font-mono">{num(c.base_imponible)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono">{num(c.monto_iva)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono">{num(c.ret_iva)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono">{num(c.air.reduce((s, a) => s + Number(a.valor), 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card2>

        {/* Ventas */}
        <Card2 title={`Ventas — agrupadas por cliente (${d.ventas.length})`} right={<CsvBtn onClick={() => exportarCSV(`ats-ventas-${anio}-${mes}`, ['TipoID', 'Identificación', 'Comprobante', '# Comp.', 'Base gravada', 'Base 0%', 'IVA'], d.ventas.map((v) => [TID[v.tp_id] || v.tp_id, v.id_cliente, TCOMP[v.tipo_comprobante] || v.tipo_comprobante, v.n_comprobantes, v.base_grav, v.base_iva0, v.monto_iva]))} />}>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Tipo ID</th><th className="py-2 pr-3">Identificación</th><th className="py-2 pr-3">Comprobante</th><th className="py-2 pr-3 text-right"># Comp.</th><th className="py-2 pr-3 text-right">Base gravada</th><th className="py-2 pr-3 text-right">Base 0%</th><th className="py-2 pr-3 text-right">IVA</th></tr></thead>
            <tbody>
              {d.ventas.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin ventas en el período</td></tr>}
              {d.ventas.map((v, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-1.5 pr-3">{TID[v.tp_id] || v.tp_id}</td>
                  <td className="py-1.5 pr-3 font-mono">{v.id_cliente}</td>
                  <td className="py-1.5 pr-3">{TCOMP[v.tipo_comprobante] || v.tipo_comprobante}</td>
                  <td className="py-1.5 pr-3 text-right font-mono">{v.n_comprobantes}</td>
                  <td className="py-1.5 pr-3 text-right font-mono">{num(v.base_grav)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono">{num(v.base_iva0)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono">{num(v.monto_iva)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card2>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Ventas por establecimiento */}
          <Card2 title="Ventas por establecimiento">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Cód. estab.</th><th className="py-2 pr-3 text-right">Ventas</th></tr></thead>
              <tbody>
                {d.ventas_estab.length === 0 && <tr><td colSpan={2} className="py-6 text-center text-muted-foreground">Sin datos</td></tr>}
                {d.ventas_estab.map((e, i) => <tr key={i} className="border-b last:border-0"><td className="py-1.5 pr-3 font-mono">{e.estab}</td><td className="py-1.5 pr-3 text-right font-mono">{num(e.ventas)}</td></tr>)}
              </tbody>
            </table>
          </Card2>

          {/* Anulados */}
          <Card2 title={`Anulados (${d.anulados.length})`}>
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Comprobante</th><th className="py-2 pr-3">Secuencial</th><th className="py-2 pr-3">Autorización</th></tr></thead>
              <tbody>
                {d.anulados.length === 0 && <tr><td colSpan={3} className="py-6 text-center text-muted-foreground">Sin anulados</td></tr>}
                {d.anulados.map((a, i) => <tr key={i} className="border-b last:border-0"><td className="py-1.5 pr-3">{TCOMP[a.tipo_comprobante] || a.tipo_comprobante}</td><td className="py-1.5 pr-3 font-mono">{a.establecimiento}-{a.punto_emision}-{a.secuencial}</td><td className="py-1.5 pr-3 font-mono text-xs">{a.autorizacion}</td></tr>)}
              </tbody>
            </table>
          </Card2>
        </div>
      </>}
    </div>
  );
}
